import React, { useState } from "react";
import { useSelector } from "react-redux";
import firebase from "firebase/compat/app";
import DataBase from "../../dataBase/liquidGuides";
import { planGuideFixes, planMessage, applyGuideFixes, readyByGuide, infoOf, featuredOf, missingGuideIds, canModify, shareOf, changesData } from "../../Common/guideFixes";

// Admin Dashboard → Guides: review and apply the fixes found while writing
// the website's city pages, one city at a time. Load the fixes file, Check,
// pick a city, untick any fix you don't want, Apply — or Apply to all cities. Fixes that need a person
// are listed but not applied; a "plan" fix replaces a whole plan's stops and times. See Common/guideFixes.js.
// 2026-10-28. "cover" and "rename_guide" fixes also write guide_info (the Library's copy), 2026-10-30.
const STATUS = {
  ready: "",
  done: "already done",
  changed: "edited since the review, left alone",
  missing: "guide not found",
  person: "needs a person in the app",
  fixed: "fixed",
  failed: "failed",
};

// Saves the fixed guide; a new cover or name also goes to guide_info, which the Library shows; a
// "featured" fix makes the guide Featured last, once its content is saved. A guide-to-be (create_guide)
// is created in the signed-in account with the app's own new-guide save.
async function saveFixed(guide, data, fixes, userInfo) {
  if (guide.create) {
    if (!userInfo?.uid) return { pass: false };
    const made = await new Promise((resolve) => DataBase.batchedWriteGuide(JSON.parse(JSON.stringify(data)), null, null, userInfo, "new", resolve));
    const id = made && made.pass ? made.new_info_ref?.id : null;
    if (!id) return { pass: false };
    Object.assign(guide, { create: false, guide_info_id: id, owner: userInfo.uid });
    if (featuredOf(fixes)) {
      const f = await new Promise((resolve) => DataBase.setGuideFeatured(id, true, resolve));
      if (!f || !f.pass) return { pass: false };
      guide.type = "Featured";
    }
    return { pass: true };
  }
  // only "share"/"featured" steps: the guide's content stays as it is
  const res = changesData(fixes) ? await new Promise((resolve) => DataBase.replaceGuideDataContent(guide.guide_id, data, resolve)) : { pass: true };
  for (const uid of shareOf(fixes)) {
    if (!res || !res.pass) break;
    const r = await new Promise((resolve) => DataBase.addGuideModifyAccess(guide.guide_info_id, uid, resolve));
    if (!r || !r.pass) return { pass: false };
    guide.access = [...(guide.access || []), uid];
  }
  const info = infoOf(fixes);
  if (res && res.pass && info && !(await DataBase.updateGuideInfoFields(guide.guide_info_id, info))) return { pass: false };
  if (res && res.pass && featuredOf(fixes) && guide.type !== "Featured") {
    const f = await new Promise((resolve) => DataBase.setGuideFeatured(guide.guide_info_id, true, resolve));
    if (!f || !f.pass) return { pass: false };
    guide.type = "Featured";
  }
  return res;
}

export default function FeaturedGuideFixPanel() {
  const userInfo = useSelector((state) => state.user_info);
  const [fixes, setFixes] = useState(null);
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState(null); // planGuideFixes rows
  const [city, setCity] = useState("");
  const [skip, setSkip] = useState(() => new Set()); // unticked fixes, "guide|n"
  const [message, setMessage] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [confirmingAll, setConfirmingAll] = useState(null); // readyByGuide result waiting for "Yes"

  const key = (r) => `${r.fix.guide_info_id}|${r.fix.n}`;

  const load = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setRows(null);
    setCity("");
    setMessage("");
    file.text().then((text) => {
      try {
        const list = JSON.parse(text).fixes;
        if (!Array.isArray(list)) throw new Error("no fixes");
        setFixes(list);
        setFileName(file.name);
        setMessage(`${list.length} fixes loaded. Click Check.`);
      } catch (err) {
        setFixes(null);
        setMessage("That file isn't a guide fixes file.");
      }
    });
  };

  const check = () => {
    setBusy(true);
    setMessage("");
    setRows(null);
    DataBase.getFeaturedGuidesData((res) => {
      setBusy(false);
      if (!res.pass) {
        setMessage("Could not read the Featured guides.");
        return;
      }
      // guides the file names that aren't Featured yet (new guides the file makes Featured)
      const missing = missingGuideIds(res.guides, fixes);
      const done = (extra) => {
        setBusy(false);
        const planned = planGuideFixes([...res.guides, ...extra], fixes);
        setRows(planned);
        // 2026-11-02: a file already applied showed an empty panel with no word why
        setMessage(planMessage(planned));
      };
      if (!missing.length) return done([]);
      setBusy(true);
      DataBase.getGuidesDataByInfoIds(missing, (more) => done(more.pass ? more.guides : []));
    });
  };

  const cities = rows ? [...new Set(rows.map((r) => r.fix.city))].sort() : [];
  const cityRows = rows ? rows.filter((r) => r.fix.city === city) : [];
  const chosen = cityRows.filter((r) => r.status === "ready" && !skip.has(key(r)));
  const countFor = (c) => rows.filter((r) => r.fix.city === c && r.status === "ready").length;

  const toggle = (r) => {
    const next = new Set(skip);
    if (next.has(key(r))) next.delete(key(r));
    else next.add(key(r));
    setSkip(next);
  };

  // Asks on the page, not with window.confirm (blocked silently in embedded browsers).
  const apply = () => {
    const uid = firebase.auth().currentUser?.uid;
    if (!chosen[0].guide.create && !canModify(chosen[0].guide, uid)) {
      setMessage("The signed-in account can't change this guide (it isn't the owner and the guide isn't shared with it).");
      return;
    }
    setMessage("");
    setConfirming(true);
  };

  const applyConfirmed = async () => {
    setConfirming(false);
    setBusy(true);
    const guide = chosen[0].guide;
    let res;
    try {
      const data = applyGuideFixes(guide.data, chosen.map((r) => r.fix));
      res = await saveFixed(guide, data, chosen.map((r) => r.fix), userInfo);
      if (res && res.pass) guide.data = data; // later Checks of this city see the fixed guide
    } catch (err) {
      console.error("Guide fix failed", guide.guide_id, err);
      res = { pass: false };
    }
    chosen.forEach((r) => (r.status = res && res.pass ? "fixed" : "failed"));
    setRows([...rows]);
    setBusy(false);
    setMessage(res && res.pass ? `${chosen.length} fixes applied to ${city}.` : `Nothing was changed in ${city}: the save failed.`);
  };

  // Every ready, ticked fix in every guide the signed-in account owns; one save per guide.
  const applyAll = () => {
    const plan = readyByGuide(rows, skip, firebase.auth().currentUser?.uid);
    if (plan.count === 0) {
      setMessage("Nothing to apply in guides that belong to the signed-in account.");
      return;
    }
    setMessage("");
    setConfirmingAll(plan);
  };

  const applyAllConfirmed = async () => {
    const { byGuide, others } = confirmingAll;
    setConfirmingAll(null);
    setBusy(true);
    let done = 0;
    let failed = 0;
    let guidesDone = 0;
    for (const [guideId, list] of byGuide) {
      const guide = list[0].guide;
      let res;
      try {
        const data = applyGuideFixes(guide.data, list.map((r) => r.fix));
        // eslint-disable-next-line no-await-in-loop
        res = await saveFixed(guide, data, list.map((r) => r.fix), userInfo);
        if (res && res.pass) guide.data = data;
      } catch (err) {
        console.error("Guide fix failed", guideId, err);
        res = { pass: false };
      }
      list.forEach((r) => (r.status = res && res.pass ? "fixed" : "failed"));
      if (res && res.pass) {
        done += list.length;
        guidesDone++;
      } else failed += list.length;
      setRows([...rows]);
      setMessage(`Applying… ${guidesDone} of ${byGuide.size} guides done.`);
    }
    setBusy(false);
    setMessage(
      `${done} fixes applied in ${guidesDone} guides${failed ? `, ${failed} failed` : ""}${others ? `, ${others} in guides owned by another account (skipped)` : ""}.`
    );
  };

  const allReady = rows ? readyByGuide(rows, skip, firebase.auth().currentUser?.uid) : null;

  return (
    <div className="admindash-lookup">
      <p className="tai-paste-title admindash-lookup-title">Featured guides: city page fixes</p>
      <p className="tai-paste-hint">
        Problems found while writing the website's city pages. Load the fixes file (guide-fixes-….json), Check, pick a
        city, untick anything you don't want, then Apply. Fixes marked "needs a person" are for you to do in the guide.
      </p>
      <div className="admindash-lookup-row">
        {/* file inputs are hidden app-wide (Modal.css); the label is the button */}
        <label className="tai-cancel-btn" style={{ margin: 0, cursor: "pointer" }}>
          Choose file…
          <input type="file" accept=".json,application/json" onChange={load} disabled={busy} aria-label="Fixes file" />
        </label>
        <button className="tai-cancel-btn" onClick={check} disabled={busy || !fixes}>
          {busy && !rows ? "Checking…" : "Check"}
        </button>
        {rows && (
          <select
            value={city}
            onChange={(e) => {
              setCity(e.target.value);
              setConfirming(false);
              setMessage("");
            }}
            aria-label="City"
          >
            <option value="">City…</option>
            {cities.map((c) => (
              <option key={c} value={c}>
                {c} ({countFor(c)} to apply)
              </option>
            ))}
          </select>
        )}
      </div>
      {allReady && allReady.count > 0 && (
        <div className="admindash-lookup-row">
          {!confirmingAll && (
            <button className="tai-primary-btn" onClick={applyAll} disabled={busy}>
              Apply to all cities ({allReady.count} fixes in {allReady.byGuide.size} guides)
            </button>
          )}
          {confirmingAll && (
            <>
              <button className="tai-primary-btn" onClick={applyAllConfirmed}>
                Yes, change {confirmingAll.byGuide.size} guides
              </button>
              <button className="tai-cancel-btn" onClick={() => setConfirmingAll(null)}>
                Cancel
              </button>
            </>
          )}
        </div>
      )}
      {fileName !== "" && <p className="tai-paste-hint">{fileName}</p>}
      {message !== "" && <p className="tai-paste-hint">{message}</p>}
      {city !== "" && (
        <div style={{ maxHeight: 420, overflowY: "auto", fontSize: 13, display: "grid", gap: 10, margin: "8px 0" }}>
          {cityRows.map((r) => (
            <label key={key(r)} style={{ display: "flex", gap: 8, alignItems: "flex-start", opacity: r.status === "ready" ? 1 : 0.7 }}>
              <input
                type="checkbox"
                className="gfx-check"
                style={{ display: "inline-block", marginTop: 3 }}
                checked={r.status === "ready" ? !skip.has(key(r)) : r.status === "done" || r.status === "fixed"}
                disabled={r.status !== "ready" || busy}
                onChange={() => toggle(r)}
              />
              <span>
                <b>{r.fix.change}</b>
                <br />
                Problem: {r.fix.issue}
                {STATUS[r.status] ? (
                  <>
                    <br />
                    <i>
                      {STATUS[r.status]}
                      {r.status === "person" && r.fix.person ? ` (${r.fix.person})` : ""}
                    </i>
                  </>
                ) : null}
              </span>
            </label>
          ))}
        </div>
      )}
      {city !== "" && chosen.length > 0 && (
        <div className="admindash-lookup-row">
          {!confirming && (
            <button className="tai-primary-btn" onClick={apply} disabled={busy}>
              {busy ? "Applying…" : `Apply ${chosen.length} to ${city}`}
            </button>
          )}
          {confirming && (
            <>
              <button className="tai-primary-btn" onClick={applyConfirmed}>
                Yes, change the {city} guide
              </button>
              <button className="tai-cancel-btn" onClick={() => setConfirming(false)}>
                Cancel
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
